import { createStore } from "vuex";
import createPersistedState from "vuex-persistedstate";
import actions from "./actions";
import getters from "./getters";
import configurator from "./modules/configurator";
import events from "./modules/events";
import experience from "./modules/experience";
import learnerTypes from "./modules/learnerTypes";
import myStudentDetail from "./modules/myStudentDetail";
import programManager from "./modules/programManager";
import programSessions from "./modules/programSessions";
import tagManager from "./modules/tags";
import university from "./modules/university";
import mutations from "./mutations";
import createState from "./state";

const stripOktaAccessToken = (state) => {
  if (!state || !state.currentUser || typeof state.currentUser !== "object") return state;

  const impersonator = state.currentUser.impersonatedBy;
  const hasOwnToken = Object.hasOwn(state.currentUser, "okta_access_token");
  // While impersonating, the actor's token rides along under `impersonatedBy` — strip it too.
  const hasImpersonatorToken =
    impersonator && typeof impersonator === "object" && Object.hasOwn(impersonator, "okta_access_token");

  if (!hasOwnToken && !hasImpersonatorToken) return state;

  // Omit token so it never hits sessionStorage; keep runtime token in-memory only.
  // eslint-disable-next-line no-unused-vars
  const { okta_access_token, ...currentUser } = state.currentUser;

  if (hasImpersonatorToken) {
    // eslint-disable-next-line no-unused-vars
    const { okta_access_token: impersonatorToken, ...impersonatedBy } = impersonator;
    currentUser.impersonatedBy = impersonatedBy;
  }

  return { ...state, currentUser };
};

const stripAnonToken = (state) => {
  if (!state || !Object.hasOwn(state, "anonToken")) return state;

  // anonToken is re-injected from the server each session; never persist it.
  // eslint-disable-next-line no-unused-vars
  const { anonToken, ...rest } = state;
  return rest;
};

const stripPersistedTokens = (state) => stripAnonToken(stripOktaAccessToken(state));

const scrubOktaAccessTokenFromStorage = (key, storage) => {
  const raw = storage.getItem(key);
  if (!raw) return undefined;

  try {
    const parsed = JSON.parse(raw);
    const scrubbed = stripOktaAccessToken(parsed);

    // If we changed anything, write back immediately to clean up existing sessions.
    if (scrubbed !== parsed) storage.setItem(key, JSON.stringify(scrubbed));

    return scrubbed;
  } catch (_) {
    return undefined;
  }
};

export default createStore({
  strict: true,
  state: createState(),
  mutations,
  actions,
  getters,
  plugins: [
    createPersistedState({
      storage: window.sessionStorage,
      getState: scrubOktaAccessTokenFromStorage,
      reducer: stripPersistedTokens,
    }),
  ],
  modules: {
    university,
    programManager,
    tagManager,
    experience,
    programSessions,
    events,
    configurator,
    learnerTypes,
    myStudentDetail,
  },
});
